import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { HelperService } from '../helper/helper.service';
import { HttpService } from '../helper/http.service';
import { searchSuggestions } from '@components/search/search-bar/search-preview/search-suggestions';
import { AuthService } from '../auth/auth.service';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AW } from '@airwallet/types';
import { SettlementViewModel, TerminalViewModel, TransactionViewModel } from '@airwallet/data';

export interface TrxFullView {
    count: number;
    owner: string;
    id: string;
    type: 'user' | 'location' | 'device' | 'customer';
}

export type FESorted = {
    [K in AW.Search.Entity['t'] as `${K}`]: AW.Search.Entity[];
};

interface RouteType {
    path: string;
    params?: object;
}

@Injectable({
    providedIn: 'root'
})
export class SearchService {
    fuzzyTimeout!: NodeJS.Timeout;

    fullSearchResult: WritableSignal<FESorted | null> = signal(null);
    fuzzySearchResult: WritableSignal<FESorted | null> = signal(null);

    hasFuzzyResult: Signal<AW.Search.HasResult> = computed(() => {
        const result = this.fuzzySearchResult();
        if (result) {
            const hasResult: boolean = Object.keys(result).some(key => result[key as keyof FESorted].length > 0);
            return hasResult ? 'HAS_RESULT' : 'NO_RESULT';
        }
        return 'NOT_SEARCHED';
    });
    hasFullSearchResult: Signal<AW.Search.HasResult> = computed(() => {
        const result = this.fullSearchResult();
        if (result) {
            const hasResult: boolean = Object.keys(result).some(key => result[key as keyof FESorted].length > 0);
            return hasResult ? 'HAS_RESULT' : 'NO_RESULT';
        }
        return 'NOT_SEARCHED';
    });

    isSearchDropdownOpened: WritableSignal<boolean> = signal(false);
    mobileSearchOpen: WritableSignal<boolean> = signal(false);

    searchQuery: WritableSignal<string> = signal('');

    searchInputValue: WritableSignal<string> = signal('');

    loadingFuzzy: WritableSignal<boolean> = signal(false); // This is used in preview (as it wants to show results before the full search is done)
    loadingFinishedSearchResults: WritableSignal<boolean> = signal(false); // This is shown on the search page (as it only wants to display full search)

    constructor(
        private httpService: HttpService,
        private helperService: HelperService,
        private authService: AuthService,
        private router: Router
    ) {
        this.listenForNavigationAwayFromSearch();
    }

    private listenForNavigationAwayFromSearch(): void {
        this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(currentEvent => {
            const isStillOnSearchPage: boolean = currentEvent.urlAfterRedirects.startsWith('/search');

            if (!isStillOnSearchPage) {
                this.resetSearch();
            }
        });
    }

    public resetSearch(): void {
        this.searchInputValue.set('');
        this.searchQuery.set('');
        this.fuzzySearchResult.set(null);
        this.fullSearchResult.set(null);
        this.isSearchDropdownOpened.set(false);
        this.loadingFuzzy.set(false);
        this.loadingFinishedSearchResults.set(false);
    }

    async performSearch(searchString: string, full?: boolean): Promise<FESorted> {
        const searchScope = specifySearch(this.cleanSearchString(searchString));
        return await (async () => {
            if (searchScope === 'fuzzy') {
                return new Promise<FESorted>(resolve => {
                    this.fuzzyTimeout = setTimeout(async () => {
                        const result = await this.httpService._http<{
                            result: FESorted;
                            trxFullView: TrxFullView;
                        }>('search', 'search', 'get', { params: { q: searchString, full: full ? 'true' : 'false' } });
                        resolve(result.result);
                    }, 300);
                });
            } else {
                if (searchScope === 'phone') searchString = this.splitPhoneNumber(this.phoneNumberFormat(searchString)).number || searchString;
                if (searchString.includes(':')) searchString = searchString.split(':')[1].trim();
                return (
                    await this.httpService._http<{
                        result: FESorted;
                        trxFullView: TrxFullView;
                    }>('search', `search/exact/${searchScope}/${encodeURIComponent(searchString)}`, 'get', { params: { full: full ? 'true' : 'false' } })
                ).result;
            }
        })();
    }

    async performFuzzySearch(searchString: string): Promise<void> {
        this.loadingFuzzy.set(true);
        this.searchQuery.set(searchString);
        if (!searchString || searchString.length < 2) {
            clearTimeout(this.fuzzyTimeout);
            this.fuzzySearchResult.set(null);
            this.loadingFuzzy.set(false);
            return;
        } else {
            // Do fuzzy search
            if (this.fuzzyTimeout) {
                clearTimeout(this.fuzzyTimeout);
            }
            const response: FESorted = await this.performSearch(searchString);
            this.fuzzySearchResult.set(response);
            this.loadingFuzzy.set(false);
        }
    }

    async fullSearch(searchString: string) {
        this.loadingFinishedSearchResults.set(true);
        const response: FESorted = await this.performSearch(searchString, true);
        this.fullSearchResult.set(response);
        this.loadingFinishedSearchResults.set(false);
        return response;
    }

    splitPhoneNumber(number: string): { code: string; number: string } {
        try {
            const phoneUtil: PhoneNumberUtil = PhoneNumberUtil.getInstance();
            const phoneNumber = phoneUtil.parse(number);
            const formattedNumber = phoneUtil.format(phoneNumber, PhoneNumberFormat.INTERNATIONAL);
            const splittedArr = formattedNumber.split(' ');
            const code = splittedArr[0];
            let combinedNumber = '';
            for (let i = 1; i < splittedArr.length; i++) {
                combinedNumber += splittedArr[i];
            }
            return { code, number: combinedNumber };
        } catch (error) {
            // console.error('search-split-phone-number-error', { error, number, uid });
            return { code: '', number: '' };
        }
    }

    highlightText(text: string | number, phoneNo?: boolean): string {
        text = text.toString();

        if (!text || !this.searchQuery() || text.startsWith(' • • • •')) {
            return text;
        }

        if (phoneNo) {
            const escapedSearchQuery = this.searchQuery()
                .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                .replace(/\s/g, '');
            const highlightedText = text.replace(/\s/g, '').replace(new RegExp(escapedSearchQuery, 'gi'), match => {
                return `<span class="highlight">${this.helperService.normalizePhoneNumber(match)}</span>`;
            });

            return highlightedText;
        } else {
            const escapedSearchQuery = this.searchQuery().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const highlightedText = text.replace(new RegExp(escapedSearchQuery, 'gi'), match => {
                return `<span class="highlight">${match}</span>`;
            });

            return highlightedText;
        }
    }

    async getPath(item: AW.Search.Entity, userUid: string): Promise<RouteType> {
        userUid = userUid.split('_operated_by_')[0];
        const route: RouteType = { path: '' };

        switch (item.t) {
            case 'location': {
                if (item.owner && item.owner !== userUid) {
                    route.path = `customers/${item.owner.split('_operated_by_')[0]}/locations/${item.id}`;
                } else {
                    route.path = `locations/${item.id}`;
                }
                route.params = { name: item.val[0] };
                break;
            }
            case 'customer': {
                route.path = `customers/${item.id}/locations`;
                route.params = { name: item.val[0] };
                break;
            }
            case 'device': {
                if (item.owner && item.owner !== userUid) {
                    route.path = `customers/${item.owner.split('_operated_by_')[0]}/locations/${item.id.split(',')[0]}/devices/${item.id.split(',')[1]}`;
                } else {
                    route.path = `locations/${item.id.split(',')[0]}/devices/${item.id.split(',')[1]}`;
                }
                route.params = { name: item.val[0] };
                break;
            }
            case 'terminal': {
                const terminal = new TerminalViewModel(item as AW.Search.TerminalEntity);
                if (this.authService.isAdmin()) {
                    route.path = `admin/monitor-health`;
                    route.params = { highlight: terminal.serial };
                } else if (this.authService.isOperator()) {
                    route.path = `health-monitor`;
                    route.params = { highlight: terminal.serial };
                } else {
                    if (item.owner && item.owner !== userUid) {
                        route.path = `customers/${terminal.owner}/locations/${terminal.location_id}`;
                    } else {
                        route.path = `locations/${terminal.location_id}`;
                    }
                    route.params = { name: terminal.serial };
                }
                break;
            }
            case 'contract':
                route.params = { id: item.id };
                route.path = `/contracts`;
                break;
            case 'settlement': {
                const settlement = new SettlementViewModel(item as AW.Search.SettlementEntity);
                route.path = ``;
                window.open(settlement.link);
                break;
            }
            case 'transaction':
                route.path = `transactions`;
                route.params = { docId: item.id, uid: item.owner };
                // route.params = { location_id: transactionDev.location_key, device_id: transactionDev.device_key, customer_uid: transactionDev.owner_uid.split('_operated_by_')[0] };
                // route.params = { location_id: transactionLoc.location_key, customer_uid: transactionLoc.owner_uid.split('_operated_by_')[0] };

                break;

            case 'user':
                route.path = `users/${item.id.split('_')[0]}`;
                route.params = { owner_uid: item.owner };
                break;
            case 'coupon':
                route.path = `coupons/${item.id}`;
                break;

            default:
                break;
        }
        return route;
    }

    updateTransactionInResults(updatedEntity: TransactionViewModel): void {
        const categoryKey = 'transaction';

        this.fullSearchResult.update(currentResults => {
            if (!currentResults) return currentResults;

            // The list is correctly typed as SearchEntity[]
            const transactionList: AW.Search.Entity[] = currentResults[categoryKey] || [];

            // The map function now correctly compares and returns the same type (SearchEntity)
            const updatedList = transactionList.map(item => {
                if (item.t === 'transaction') {
                    const t = new TransactionViewModel(item as AW.Search.TransactionEntity);
                    if (t.payment_id === updatedEntity.payment_id) {
                        return updatedEntity; // Now it's an Entity, so the types match
                    }
                }
                return item;
            });

            // Return the new, updated state object
            return {
                ...currentResults,
                [categoryKey]: updatedList
            };
        });
    }

    cleanSearchString(searchString: string): string {
        let trimmedSearchString = '';

        for (const suggestion of searchSuggestions) {
            // trim spaces around the search query
            trimmedSearchString = searchString.includes(suggestion.short_code) ? `${suggestion.short_code}${searchString.split(suggestion.short_code)[1].trim()}` : searchString.trim();
        }

        if (
            // The backend renocgnizes the following string without prefix, but the ui wants to show it as an option
            trimmedSearchString.startsWith('a:') ||
            trimmedSearchString.startsWith('u:')
        ) {
            return trimmedSearchString.substring(2);
        } else if (trimmedSearchString.endsWith(':')) {
            // if prefix but nothing after it, then return empty string
            return '';
        } else {
            return trimmedSearchString;
        }
    }

    phoneNumberFormat(str: string): string {
        let formattedStr: string = str;
        if (str.at(0) === '+' || str.includes('u:')) {
            // is most likely a phone number
            formattedStr = formattedStr.replace(/\(.*?\)/g, '');
        }
        return formattedStr;
    }
}

function specifySearch(searchString: string): AW.Search.Scopes {
    if (searchString.startsWith('pi_') || searchString.startsWith('cp-') || searchString.startsWith('wp-') || searchString.startsWith('co-')) {
        return 'payment';
    }
    if (searchString.startsWith('AW') && (searchString.endsWith('R1') || searchString.endsWith('R2'))) {
        return 'device';
    }
    if (searchString.startsWith('a:') || searchString.startsWith('WPC') || searchString.startsWith('STRM')) {
        return 'terminal';
    }
    if (searchString.startsWith('po_') || searchString.startsWith('po-') || searchString.startsWith('s:')) {
        return 'settlement';
    }
    if (isPhoneNo(searchString)) {
        return 'phone';
    }
    if (searchString.startsWith('l4:')) {
        return 'last4';
    }
    if (searchString.startsWith('cl:')) {
        return 'custom_location_id';
    }
    if (searchString.startsWith('l:')) {
        return 'location';
    }
    if (searchString.startsWith('c:')) {
        return 'contract';
    }
    if (searchString.startsWith('u:')) {
        return 'phone';
    }
    if (searchString.startsWith('cc:')) {
        return 'coupon_code';
    }
    return 'fuzzy';
}

export function isPhoneNo(s: string): boolean {
    // Replace all spaces with empty string.
    s = s.replace(/[\s]/g, '');
    const allowedChars = new Set('0123456789()+-');

    // Check if the string is empty
    if (!s) {
        return false;
    }

    // Check if the string starts with '+' and remove it
    if (s[0] === '+') {
        s = s.slice(1);
    }

    // Check if all characters in the string are allowed
    for (const char of s) {
        if (!allowedChars.has(char)) {
            return false;
        }
    }

    // Check if there are parentheses in balanced pairs
    let parenthesesCount = 0;
    for (const char of s) {
        if (char === '(') {
            parenthesesCount++;
        } else if (char === ')') {
            parenthesesCount--;
            if (parenthesesCount < 0) {
                return false;
            }
        }
    }

    // Check if the string contains only digits or dashes after removing '+' and parentheses
    const digitsAndDashesOnly: string = s.replace(/[()]/g, '');
    return /^[\d-]+$/.test(digitsAndDashesOnly);
}
